import React, { useEffect, useRef, useState } from "react";
import { Wrapper } from "./style";

const SYSTEM_PROMPT = `You are the sellerview.AI Copilot - a profit analyst for Amazon FBA/FBM sellers inside Sellerview.ai. Sellerview gives sellers SKU-level P&L, TACoS/ACoS clarity, and profit leak detection.
Style: Direct, sharp, no fluff. Like a CFO who knows Amazon. Lead with money impact. Concise, conversational. End with a clear action. No jargon.`;

const DEMO_RESPONSES = {
  profit: {
    intro: "Here's your profit picture for this month:",
    metrics: [
      { l: "Net Profit", v: "₹1,42,830", c: "nv" },
      { l: "vs Last Month", v: "−8.3%", c: "co", ch: "dn", chv: "▼ ₹12,860" },
      { l: "Net Margin", v: "14.2%", c: "nv" },
      { l: "Total Revenue", v: "₹10,05,900", c: "gr" },
    ],
    follow:
      "The dip is from two places - FBA storage fees jumped 22% and your top ASIN return rate hit 11%. Want me to identify the exact SKUs dragging profit down?",
  },
  tacos: {
    intro: "Your ad performance this month:",
    metrics: [
      { l: "TACoS", v: "18.4%", c: "co" },
      { l: "ACoS", v: "31.2%", c: "co" },
      { l: "Total Ad Spend", v: "₹68,400", c: "nv" },
      { l: "Ad-Attributed Sales", v: "₹3,71,700", c: "gr" },
    ],
    follow:
      "Your TACoS is above the healthy 12-15% zone. Three SKUs are spending heavily with near-zero incremental profit. Want me to show which campaigns to pause first?",
  },
};

const quickAsks = [
  { text: "Net profit this month?", icon: "💰", query: "What is my net profit this month?" },
  { text: "Which SKUs are losing money?", icon: "🩸", query: "Which SKUs are bleeding money?" },
  { text: "What's my current TACoS?", icon: "📊", query: "What is my current TACoS?" },
  { text: "Return losses this month?", icon: "↩️", query: "How much am I losing to returns this month?" },
  { text: "Top profitable SKUs?", icon: "⭐", query: "What are my top 5 most profitable SKUs?" },
];

const strategicAsks = [
  { text: "Revenue grew, profit didn't?", icon: "📈", query: "My revenue grew but profit didn't. Why?" },
  { text: "Scale vs. kill - which SKUs?", icon: "⚡", query: "Which SKUs should I scale and which should I kill?" },
  { text: "True margin per unit?", icon: "🔍", query: "What's my true net margin per unit after all Amazon fees and ad costs?" },
  { text: "Worst-performing campaigns?", icon: "🎯", query: "Which ad campaigns are spending the most but contributing least to profit?" },
  { text: "Where are my profit leaks?", icon: "🕳️", query: "Where are my biggest profit leaks hiding?" },
];

const getDemoResponse = (query) => {
  const q = query.toLowerCase();
  if (q.match(/profit|margin|revenue|money|earn/)) return DEMO_RESPONSES.profit;
  if (q.match(/tacos|acos|ad |campaign|spend/)) return DEMO_RESPONSES.tacos;
  return null;
};

function MetricGrid({ metrics }) {
  return (
    <div className="mgrid">
      {metrics.map((m) => (
        <div key={m.l} className="mc">
          <div className="ml">{m.l}</div>
          <div className={`mv ${m.c}`}>{m.v}</div>
          {m.ch ? <div className={`mch ${m.ch}`}>{m.chv}</div> : null}
        </div>
      ))}
    </div>
  );
}

function BotAvatar() {
  return (
    <div className="av bot">
      <div className="bot-mark"><img src="/assets/media/sellerView-favicon.png" style={{width: 24, height: 24, objectFit: 'cover', filter: "brightness(0) invert(1)"}}/></div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  if (isUser) {
    return (
      <div className="row user">
        <div className="av user"><img src="/assets/media/avatars/blank.png" style={{objectFit: 'cover'}}/></div>
        <div className="bubble user">{message.content}</div>
      </div>
    );
  }

  return (
    <div className="row">
      <BotAvatar />
      <div className="bubble bot">
        <div dangerouslySetInnerHTML={{ __html: message.content.replace(/\n/g, "<br/>") }} />
        {message.metrics ? <MetricGrid metrics={message.metrics} /> : null}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="row">
      <BotAvatar />
      <div className="bubble bot">
        <div className="typ">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

export default function AICopilot() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const adjustTextarea = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 110)}px`;
  };

  const addMessage = (role, content, metrics) => {
    setMessages((prev) => [...prev, { role, content, metrics }]);
  };

  const handleSend = async (override) => {
    const query = (override ?? inputValue).trim();
    if (!query || isTyping) return;

    setInputValue("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    addMessage("user", query);
    setIsTyping(true);
    await new Promise((r) => setTimeout(r, 900));

    const demo = getDemoResponse(query);
    if (demo) {
      setIsTyping(false);
      addMessage("assistant", demo.intro, demo.metrics);
      setTimeout(() => addMessage("assistant", demo.follow), 350);
      return;
    }

    const fallback = SYSTEM_PROMPT.includes("profit analyst")
      ? "I reviewed current trend signals. Start by fixing margin-negative SKUs, then cut high TACoS campaigns. Want the top 5 SKUs ranked by profit opportunity?"
      : "I analyzed current trends. Want a detailed SKU-level breakdown?";

    setIsTyping(false);
    addMessage("assistant", fallback);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const showWelcome = messages.length === 0 && !isTyping;

  return (
    <Wrapper style={{marginTop: 30}} className="">
      <header>
        <div className="logo-wrap">
          <div className="logo-square">
            <img src="/assets/media/sellerView-favicon.png" alt="Sellerview Logo" />
          </div>
          <div className="logo-text">
            sellerview.AI<span> Copilot</span>
          </div>
     
        </div>
        <div className="hdr-right">
          <div className="badge">BETA</div>
          <div className="status">
            <div className="dot" />
            Online
          </div>
        </div>
      </header>

      <div className="chat-wrap">
        <aside className="sidebar">
          <div className="s-label">Quick Asks</div>
          {quickAsks.map((item) => (
            <button key={item.text} type="button" className="qq" onClick={() => handleSend(item.query)}>
              {/* <span>{item.icon}</span> */}
              <span>{item.text}</span>
            </button>
          ))}

          <div className="s-divider" />

          <div className="s-label">Strategic</div>
          {strategicAsks.map((item) => (
            <button key={item.text} type="button" className="qq nv" onClick={() => handleSend(item.query)}>
              {/* <span>{item.icon}</span> */}
              <span>{item.text}</span>
            </button>
          ))}
        </aside>

        <div className="chat-main">
          <div className="messages">
            {showWelcome ? (
              <div className="welcome">
                <div className="w-icon">
                  <div className="bot-mark">
                    <img src="/assets/media/sellerView-favicon.png" alt="AI Bot" style={{ width: 48, height: 48, filter: "brightness(0) invert(1)" }} />
               
                  </div>
             
                </div>
                <h2>
                  sellerview.AI <span>Copilot</span>
                </h2>
                <p>
                  Ask me anything about your Amazon business - margins, TACoS, ad spend, returns, and which SKUs are
                  actually making you money.
                </p>
                <div className="chips">
                  <button type="button" className="chip" onClick={() => handleSend("What is my net profit this month?")}>
                    Net profit
                  </button>
                  <button type="button" className="chip" onClick={() => handleSend("What is my current TACoS?")}>
                    TACoS
                  </button>
                  <button type="button" className="chip" onClick={() => handleSend("Where are my biggest profit leaks?")}>
                    Profit leaks
                  </button>
                  <button
                    type="button"
                    className="chip"
                    onClick={() => handleSend("Which SKUs should I scale and which should I kill?")}
                  >
                    SKU strategy
                  </button>
                </div>
              </div>
            ) : null}

            {messages.map((msg, idx) => (
              <MessageBubble key={`${msg.role}-${idx}`} message={msg} />
            ))}
            {isTyping ? <TypingIndicator /> : null}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-bar">
            <div className="irow">
              <textarea
                ref={textareaRef}
                rows={1}
                value={inputValue}
                placeholder="Ask about your margins, TACoS, SKUs, returns..."
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  adjustTextarea();
                }}
              />
              <button type="button" className="sbtn" onClick={() => handleSend()} disabled={!inputValue.trim() || isTyping}>
                <svg viewBox="0 0 24 24">
                  <path d="M2 12L22 2L14 22L11 13L2 12Z" />
                </svg>
              </button>
            </div>
            <div className="hint">
              Powered by <strong>sellerview.AI Copilot</strong> - Your data stays private and encrypted
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}